import React from 'react';
import { Modal } from 'pink-lava-ui';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { ModalProps } from 'pink-lava-ui/types';

interface ResponsiveModalProps extends ModalProps {
  fullscreenMaxBreakpoint?: 'sm' | 'md' | 'lg' | 'xl' | 'always';
}

export const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  fullscreenMaxBreakpoint = 'sm',
  classNames,
  width,
  ...rest
}) => {
  const { isSmAndUp, isMdAndUp, isLgAndUp, isXlAndUp } = useBreakpoint();

  const shouldBeFullscreen = React.useMemo(() => {
    if (fullscreenMaxBreakpoint === 'always') return true;
    if (fullscreenMaxBreakpoint === 'sm' && !isSmAndUp) return true;
    if (fullscreenMaxBreakpoint === 'md' && !isMdAndUp) return true;
    if (fullscreenMaxBreakpoint === 'lg' && !isLgAndUp) return true;
    if (fullscreenMaxBreakpoint === 'xl' && !isXlAndUp) return true;
    return false;
  }, [fullscreenMaxBreakpoint, isSmAndUp, isMdAndUp, isLgAndUp, isXlAndUp]);

  const modalClassNames: ModalProps['classNames'] = React.useMemo(() => {
    if (shouldBeFullscreen) {
      return {
        content: '!w-[100vw] !h-[calc(100dvh)] p-0! !rounded-none',
        body: 'flex flex-col h-[calc(100dvh-56px)]! w-full!',
        header: 'mb-0! border-b-1! border-gray-300!',
        ...(classNames || {}),
      };
    }
    return classNames;
  }, [shouldBeFullscreen, classNames]);

  const modalWidth = React.useMemo(() => {
    if (shouldBeFullscreen) {
      return 'auto';
    }
    return width;
  }, [shouldBeFullscreen, width]);

  const modalClassName = React.useMemo(() => {
    if (shouldBeFullscreen) {
      return '!fixed !inset-0 !z-[1000] !m-0 !p-0 !max-w-none !max-h-none !w-screen !h-screen';
    }
    return rest.className;
  }, [shouldBeFullscreen, rest.className]);

  return (
    <Modal
      classNames={modalClassNames}
      width={modalWidth}
      className={modalClassName}
      centered={!shouldBeFullscreen}
      {...rest}
      title={shouldBeFullscreen ? <div className="py-3 px-4 h-[56px] flex items-center">{rest.title}</div> : rest.title}
    />
  );
};

export default ResponsiveModal;
